import {
  GENDER_TYPE_FEMALE,
  GENDER_TYPE_MALE,
  IN_NETWORK,
  IN_NETWORK_KEY,
  OUT_OF_NETWORK,
  OUT_OF_NETWORK_KEY,
  UPCOMING_BOOKING_STATUSES,
} from '../../../../constants/index';
import { TileUsers, TileRules, TileType, TileIds } from './tileEnums';
import { isInMarketplaceProfile } from '../Home/util/get_care';
import {
  hasPharmacyInfo,
  hasInsuranceInfo,
  hasMultipleProfiles,
} from '../../../../core/util/account';
import { hasTileBeenDismissedForUserProfile } from './tiles';
import { getAgeFromBirthDate } from '../../../../core/util/date';
import { isEmptyObject, safeGet } from '../../../../core/util/object';
import {
  isInsuranceCarrierInCustomUIGroup,
  isInsuranceCarrierOfType,
} from '../../../../core/util/insurance';
import { InsurerCodes } from '../../../../constants/insurance';
import { isEmptyArray } from '../../../../core/util/array';
import { getInsuranceFeedback, FEEDBACK_NEGATIVE } from '../Insurance/util/insuranceInsights';
import {
  getInNetworkFamilyDeductible,
  getOutOfNetworkFamilyDeductible,
  getInNetworkFamilyOutOfPocketMax,
  getOutOfNetworkFamilyOutOfPocketMax,
  getInNetworkIndividualDeductible,
  getInNetworkIndividualOutOfPocketMax,
  getOutOfNetworkIndividualOutOfPocketMax,
  getOutOfNetworkIndividualDeductible,
  getCopay,
  getCoinsurance,
} from '../Insurance/util/insuranceDetails';
import { isEmpty } from '../../../../core/util/empty';
import { isNativeApp } from '../../../../core/util/device';
import { shouldRenderDirectTelemedFlow } from '../../../../core/util/location';
import {
  EMERGENCY_ROOM_VALUE,
  PRIMARY_CARE_PHYSICIAN_VALUE,
  SPECIALIST_VALUE,
  URGENT_CARE_VALUE,
} from '../../../Home/Tiles/ProviderTypes';

class TileFilter {
  static filter = (
    tile: any,
    userProfile: any,
    locations: any,
    accountSummary: any,
    insuranceDetails: any
  ) => {
    if (tile.absolutelyAlwaysShowNoMatterTheConsequences) return true;

    let hasTelemedLocation = !!locations.telemed;
    let hasRecentLocation = !!locations.recent;
    let isInMarketplace = isInMarketplaceProfile(userProfile);
    let insuranceProfile: any = null;

    if (
      accountSummary &&
      !isEmptyArray(accountSummary.insurance_profiles) &&
      userProfile.insurance_profile_id
    ) {
      insuranceProfile = accountSummary.insurance_profiles.find(
        (p: any) => p.insurance_profile_id === userProfile.insurance_profile_id
      );
    }

    if (tile.visibility === TileUsers.IN_MARKET && !isInMarketplace) {
      return false;
    }

    if (tile.visibility === TileUsers.OUT_OF_MARKET && isInMarketplace) {
      return false;
    }

    if (tile.tileType === TileType.PROFILE_CLINIC && !hasRecentLocation) {
      return false;
    }

    if (tile.tileType === TileType.PROFILE_TELEMEDICINE && !hasTelemedLocation) {
      return false;
    }

    // Unsure if this is idea, or just returning true is better. I put this in because
    // the current error that gets thrown if you don't have a rule is very cryptic.
    if (!tile.rules) throw new Error('Tile MUST have a default ruleset');

    return tile.rules.every((rule: any) => {
      switch (rule) {
        case TileRules.VISIT_BOOKED:
          return UPCOMING_BOOKING_STATUSES.includes(tile.booking.status);

        case TileRules.VISIT_COMPLETED:
          return !UPCOMING_BOOKING_STATUSES.includes(tile.booking.status);

        case TileRules.HAS_NO_BOOKINGS:
          return safeGet(userProfile)('bookings.total') === 0;

        case TileRules.HAS_BOOKINGS:
          return safeGet(userProfile)('bookings.total') > 0;

        case TileRules.IS_NOT_TELEMED_IN_SCHOOL_LOCATIONS:
          return safeGet(tile.location) && !shouldRenderDirectTelemedFlow(tile.location);

        case TileRules.IS_NOT_CHILD:
          if (userProfile && userProfile.birth_date) {
            const age = getAgeFromBirthDate(userProfile.birth_date);
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            if (age < 18) {
              return false;
            }
          }

          break;

        case TileRules.IS_NOT_MALE:
          if (
            userProfile &&
            userProfile.birth_sex &&
            userProfile.birth_sex.toLowerCase() === GENDER_TYPE_MALE.toLowerCase()
          ) {
            return false;
          }

          break;

        case TileRules.IS_WOMAN_OVER_39:
          return (
            userProfile &&
            userProfile.birth_sex &&
            userProfile.birth_sex.toLowerCase() === GENDER_TYPE_FEMALE.toLowerCase() &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) > 39
          );

        case TileRules.IS_WOMAN_OVER_65:
          return (
            userProfile &&
            userProfile.birth_sex &&
            userProfile.birth_sex.toLowerCase() === GENDER_TYPE_FEMALE.toLowerCase() &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) > 65
          );

        case TileRules.IS_AGE_50_75:
          return (
            userProfile &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) >= 50 &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) <= 75
          );

        case TileRules.IS_WOMAN_AGE_18_65:
          return (
            userProfile &&
            userProfile.birth_sex &&
            userProfile.birth_sex.toLowerCase() === GENDER_TYPE_FEMALE.toLowerCase() &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) >= 18 &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) <= 65
          );

        case TileRules.IS_WOMAN_AGE_25_42:
          return (
            userProfile &&
            userProfile.birth_sex &&
            userProfile.birth_sex.toLowerCase() === GENDER_TYPE_FEMALE.toLowerCase() &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) >= 25 &&
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            getAgeFromBirthDate(userProfile.birth_date) <= 42
          );

        case TileRules.HAS_NO_PHARMACY:
          return !hasPharmacyInfo(userProfile);

        case TileRules.HAS_NO_INSURANCE:
          return !hasInsuranceInfo(userProfile);

        case TileRules.HAS_INSURANCE:
          return hasInsuranceInfo(userProfile);

        case TileRules.ANOTHER_PROFILE_ADDED:
          if (hasMultipleProfiles(accountSummary)) {
            return false;
          }

          break;

        case TileRules.TILE_DISMISSED:
          if (hasTileBeenDismissedForUserProfile(tile.id, userProfile)) {
            return false;
          }

          break;

        case TileRules.FULL_INSURANCE_DATA: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierInCustomUIGroup(insuranceProfile.insurer_code)
          );
        }

        case TileRules.IS_INSURANCE_AETNA: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.AETNA)
          );
        }

        case TileRules.IS_INSURANCE_CIGNA: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.CIGNA)
          );
        }

        case TileRules.IS_INSURANCE_UNITED: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.UNITED_HEALTH_CARE)
          );
        }

        case TileRules.IS_INSURANCE_BCBS_CA: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.BLUE_SHIELD_CA)
          );
        }

        case TileRules.IS_INSURANCE_BCBS_TX: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.BLUE_SHIELD_TX)
          );
        }

        case TileRules.IS_INSURANCE_BCBS: {
          return (
            insuranceProfile &&
            !isEmptyObject(insuranceProfile.facesheet) &&
            (isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.BLUE_CROSS) ||
              isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.BLUE_SHIELD) ||
              isInsuranceCarrierOfType(insuranceProfile.insurer_code, InsurerCodes.BCBS))
          );
        }

        case TileRules.INSURANCE_INSIGHT_HIDDEN: {
          return getInsuranceFeedback(tile.id, userProfile.user_profile_id) !== FEEDBACK_NEGATIVE;
        }

        case TileRules.NO_FEEDBACK_GIVEN: {
          return getInsuranceFeedback(tile.id, userProfile.user_profile_id) === null;
        }

        case TileRules.HAS_INDIVIDUAL_DEDUCTIBLE_DATA:
          return (
            !isEmpty(getInNetworkIndividualDeductible(insuranceDetails)) &&
            !isEmpty(getOutOfNetworkIndividualDeductible(insuranceDetails))
          );

        case TileRules.HAS_FAMILY_DEDUCTIBLE_DATA:
          return (
            !isEmpty(getInNetworkFamilyDeductible(insuranceDetails)) &&
            !isEmpty(getOutOfNetworkFamilyDeductible(insuranceDetails))
          );

        case TileRules.HAS_INDIVIDUAL_OOP_MAX_DATA:
          return (
            !isEmpty(getInNetworkIndividualOutOfPocketMax(insuranceDetails)) &&
            !isEmpty(getOutOfNetworkIndividualOutOfPocketMax(insuranceDetails))
          );

        case TileRules.HAS_FAMILY_OOP_MAX_DATA:
          return (
            !isEmpty(getInNetworkFamilyOutOfPocketMax(insuranceDetails)) &&
            !isEmpty(getOutOfNetworkFamilyOutOfPocketMax(insuranceDetails))
          );

        case TileRules.HAS_NO_COPAY_DATA: {
          return (
            TileFilter.isCopayEmpty(insuranceDetails, URGENT_CARE_VALUE) &&
            TileFilter.isCopayEmpty(insuranceDetails, EMERGENCY_ROOM_VALUE) &&
            TileFilter.isCopayEmpty(insuranceDetails, SPECIALIST_VALUE) &&
            TileFilter.isCopayEmpty(insuranceDetails, PRIMARY_CARE_PHYSICIAN_VALUE)
          );
        }

        case TileRules.HAS_URGENT_CARE_COPAY_DATA: {
          return !TileFilter.isCopayEmpty(insuranceDetails, URGENT_CARE_VALUE);
        }

        case TileRules.HAS_EMERGENCY_ROOM_COPAY_DATA: {
          return !TileFilter.isCopayEmpty(insuranceDetails, EMERGENCY_ROOM_VALUE);
        }

        case TileRules.HAS_PCP_COPAY_DATA: {
          return !TileFilter.isCopayEmpty(insuranceDetails, PRIMARY_CARE_PHYSICIAN_VALUE);
        }

        case TileRules.HAS_SPECIALIST_COPAY_DATA: {
          return !TileFilter.isCopayEmpty(insuranceDetails, SPECIALIST_VALUE);
        }

        case TileRules.HAS_FEWER_THAN_THREE_COPAYS: {
          return (
            [
              URGENT_CARE_VALUE,
              EMERGENCY_ROOM_VALUE,
              PRIMARY_CARE_PHYSICIAN_VALUE,
              SPECIALIST_VALUE,
            ].filter((value) => !TileFilter.isCopayEmpty(insuranceDetails, value)).length < 3
          );
        }

        case TileRules.IS_NOT_NATIVE_APP: {
          return !isNativeApp();
        }

        default:
          break;
      }

      return true;
    });
  };

  static isCopayEmpty = (insuranceDetails: any, providerType: any) => {
    return (
      !getCopay(insuranceDetails, providerType, IN_NETWORK_KEY) &&
      !getCoinsurance(insuranceDetails, providerType, IN_NETWORK_KEY) &&
      !getCopay(insuranceDetails, providerType, OUT_OF_NETWORK_KEY) &&
      !getCoinsurance(insuranceDetails, providerType, OUT_OF_NETWORK_KEY)
    );
  };
}

export default TileFilter;
